import React from "react";
import { useRegistrarCaso } from "../hooks/useRegistrarCaso";

const RegistrarCaso = ({ selectedPaciente, setView, showToast, setSelectedPaciente, initialData, isEditMode, onCancel, activeProjectId }) => {
    const { effectivePaciente, parasitos, observaciones, setObservaciones, handleEggChange, handleResultChange, handleSubmit } =
        useRegistrarCaso(selectedPaciente, initialData, isEditMode, activeProjectId, showToast, setSelectedPaciente, onCancel, setView);

    if (!effectivePaciente) return null;

    return (
        <div className="animate__animated animate__fadeIn">
            {/* Cabecera del Análisis */}
            <div className="mb-4 d-flex justify-content-between align-items-center bg-white p-4 shadow-sm border-start border-4 border-success" style={{ borderRadius: '16px' }}>
                <div>
                    <h4 className="fw-bold text-dark m-0">
                        {isEditMode ? "REVISIÓN DE EXPEDIENTE CLÍNICO" : "REGISTRO DE HALLAZGOS LABORATORIALES"}
                    </h4>
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <span className="badge bg-light text-success border border-success border-opacity-25 py-2 px-3" style={{ fontSize: '0.9rem' }}>
                            <i className="bi bi-person-check-fill me-2"></i>
                            SUJETO: {effectivePaciente.nombre}
                        </span>
                        <span className="badge bg-light text-muted border py-2 px-3 font-monospace" style={{ fontSize: '0.85rem' }}>
                            DOC: {effectivePaciente.documento}
                        </span>
                    </div>
                </div>
                <div className="text-end">
                    <div className="text-muted smaller fw-bold text-uppercase">Motor de Clasificación</div>
                    <div className="fw-bold text-success"><i className="bi bi-cpu-fill me-1"></i>V1.0 ACTIVO</div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row g-4">
                    {Object.keys(parasitos).map(name => {
                        const isPos = parasitos[name].result === 'POSITIVO';
                        const intensity = parasitos[name].intensity;
                        const intensityColor = intensity === 'ALTA' ? 'danger' : intensity === 'MEDIA' ? 'warning' : 'success';

                        return (
                            <div key={name} className="col-md-4">
                                <div className={`card h-100 border-0 shadow-sm transition-all animate__animated animate__zoomIn`} style={{ borderRadius: '24px', overflow: 'hidden' }}>
                                    <div className={`p-3 text-center fw-bold text-white shadow-sm ${isPos ? 'bg-danger bg-opacity-75' : 'bg-success bg-opacity-75'}`}>
                                        <i className={`bi ${isPos ? 'bi-bug-fill' : 'bi-shield-fill-check'} me-2`}></i>
                                        {name.toUpperCase()}
                                    </div>
                                    <div className="card-body p-4 pt-4">
                                        {/* Selectores de Resultado */}
                                        <div className="d-flex gap-2 mb-4">
                                            <button
                                                type="button"
                                                className={`btn flex-grow-1 fw-bold py-2 ${isPos ? 'btn-danger shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => handleResultChange(name, 'POSITIVO')}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                POSITIVO (+)
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn flex-grow-1 fw-bold py-2 ${!isPos ? 'btn-success shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => handleResultChange(name, 'NEGATIVO')}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                NEGATIVO (-)
                                            </button>
                                        </div>

                                        {/* Input de Huevos */}
                                        <div className="mb-4">
                                            <label className="form-label small fw-bold text-muted mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Conteo de Huevos (Lámina)</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-2 border-end-0" style={{ borderRadius: '12px 0 0 12px' }}>
                                                    <i className="bi bi-microscope text-muted"></i>
                                                </span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-lg border-2 text-center font-monospace fw-bold"
                                                    style={{ borderRadius: '0 12px 12px 0' }}
                                                    value={parasitos[name].eggs}
                                                    onChange={e => handleEggChange(name, e.target.value)}
                                                    disabled={parasitos[name].result === 'NEGATIVO'}
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>

                                        {/* Métricas Derivadas */}
                                        <div className="bg-light p-3 rounded-4 border border-opacity-10 text-center">
                                            <div className="row g-2 align-items-center">
                                                <div className="col-6 border-end">
                                                    <div className="smaller text-muted fw-bold">HPG CALC.</div>
                                                    <div className="fs-5 fw-bold text-dark font-monospace">{parasitos[name].hpg}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="smaller text-muted fw-bold">INTENSIDAD</div>
                                                    <span className={`badge bg-${intensityColor} bg-opacity-10 text-${intensityColor} mt-1 w-100 py-1`} style={{ borderRadius: '6px' }}>
                                                        {intensity || "---"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Observaciones y Acciones */}
                    <div className="col-12 mt-2">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '24px' }}>
                            <div className="card-body p-4">
                                <label className="form-label fw-bold text-dark mb-2 text-uppercase smaller" style={{ letterSpacing: '1px' }}>Observaciones Clínicas / Notas de Campo</label>
                                <textarea
                                    className="form-control border-2"
                                    rows="3"
                                    style={{ borderRadius: '16px' }}
                                    value={observaciones}
                                    onChange={e => setObservaciones(e.target.value)}
                                    placeholder="Ingrese cualquier hallazgo atípico o condición ambiental relevante..."
                                ></textarea>
                            </div>
                            <div className="card-footer bg-white border-0 p-4 pt-0 d-flex justify-content-end gap-3">
                                <button
                                    type="button"
                                    className="btn btn-light fw-bold px-4 py-2"
                                    style={{ borderRadius: '12px' }}
                                    onClick={onCancel || (() => setView("home"))}
                                >
                                    DESCARTAR ANÁLISIS
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success fw-bold px-5 py-2 shadow-sm d-flex align-items-center gap-2"
                                    style={{ borderRadius: '12px' }}
                                >
                                    <i className="bi bi-file-earmark-medical-fill fs-5"></i>
                                    {isEditMode ? "GUARDAR CAMBIOS CIENTÍFICOS" : "FINALIZAR REGISTRO CLÍNICO"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RegistrarCaso;

